<script setup lang="ts">
  import { ref, watch, onBeforeMount, provide, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import {
    ChevronDownIcon,
    ChevronUpIcon,
    EnvelopeIcon,
    DocumentDuplicateIcon,
    BellAlertIcon,
    CalendarIcon,
    CogIcon,
    CodeBracketIcon,
    LinkIcon,
    Bars4Icon,
    ComputerDesktopIcon,
    AtSymbolIcon,
    DocumentIcon,
    ServerIcon
  } from '@heroicons/vue/24/outline'
  import { BotIcon, GlobeIcon, MessageCircleIcon } from 'lucide-vue-next'
  import { Palette, FileSignature, BracketsIcon } from 'lucide-vue-next'
  import { SETTING_PAGES, SETTING_TOPICS } from '@/constants/settings/topics'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import type { CustomerUser as User } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  const router = useRouter()
  const route = useRoute()
  const fetchUser = useUserStore().fetchUser
  const user = computed<User>(() => useUserStore().user as User)

  export interface Page {
    name: string
    page: string
  }

  export interface Setting {
    name: string
    icon: unknown
    pages: Page[]
  }

  export interface Topic {
    name: string
    topic: string // You can make this more specific, e.g., set it to SETTING_TOPICS enum
    icon: unknown
    settings: Setting[]
  }

  // Now declare data correctly using these interfaces

  const availableTopics = ref<Topic[]>([
    {
      name: 'E-Mails',
      topic: SETTING_TOPICS.EMAILS,
      icon: EnvelopeIcon, // Icons should fit your actual icon system
      settings: [
        {
          name: 'JUPUS E-Mail Adresse',
          icon: AtSymbolIcon,
          pages: [{ name: 'JUPUS E-Mail Adresse', page: SETTING_PAGES.EMAILS.JUPUS_EMAIL }]
        },
        {
          name: 'Vorlagen',
          icon: DocumentDuplicateIcon,
          pages: [{ name: 'Vorlagen', page: SETTING_PAGES.EMAILS.TEMPLATES }]
        },
        {
          name: 'Design',
          icon: Palette,
          pages: [{ name: 'Design', page: SETTING_PAGES.EMAILS.DESIGN }]
        },
        {
          name: 'Signatur',
          icon: FileSignature,
          pages: [{ name: 'Signatur', page: SETTING_PAGES.EMAILS.SIGNATURES }]
        },
        {
          name: 'Platzhalter',
          icon: BracketsIcon,
          pages: [{ name: 'Platzhalter', page: SETTING_PAGES.EMAILS.PLACEHOLDERS }]
        },
        {
          name: 'Benachrichtigungen',
          icon: BellAlertIcon,
          pages: [{ name: 'Benachrichtigungen', page: SETTING_PAGES.EMAILS.NOTIFICATIONS }]
        }
      ]
    },
    {
      name: 'Künstliche Intelligenz',
      topic: SETTING_TOPICS.AI,
      icon: BotIcon,
      settings: [
        {
          name: 'Grundeinstellungen',
          icon: CogIcon,
          pages: [{ name: 'Grundeinstellungen', page: SETTING_PAGES.AI.BASE_SETTINGS }]
        },
        {
          name: 'Dokumentenvorlagen',
          icon: DocumentDuplicateIcon,
          pages: [{ name: 'Vorlagen', page: SETTING_PAGES.AI.DOCUMENT_GENERATION_TEMPLATES }]
        },
        {
          name: 'Promptvorlagen',
          icon: MessageCircleIcon,
          pages: [{ name: 'Promptvorlagen', page: SETTING_PAGES.AI.PROMPT_TEMPLATES }]
        }
      ]
    },
    {
      name: 'Kanzleisoftware',
      topic: SETTING_TOPICS.LAW_OFFICE_SOFTWARE,
      icon: ServerIcon,
      settings: [
        {
          name: 'Actaport',
          icon: GlobeIcon,
          pages: [{ name: 'Actaport', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ACTAPORT }]
        },
        {
          name: 'Advolux',
          icon: ComputerDesktopIcon,
          pages: [{ name: 'Advolux', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ADVOLUX }]
        },
        {
          name: 'Advoware',
          icon: ComputerDesktopIcon,
          pages: [{ name: 'Advoware', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ADVOWARE }]
        },
        {
          name: 'Annotext',
          icon: ComputerDesktopIcon,
          pages: [{ name: 'Annotext', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ANNOTEXT }]
        },
        {
          name: 'Jurnodes',
          icon: GlobeIcon,
          pages: [{ name: 'Jurnodes', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.JURNODES }]
        },
        {
          name: 'Kleos',
          icon: GlobeIcon,
          pages: [{ name: 'Kleos', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.KLEOS }]
        },
        {
          name: 'Lawfirm',
          icon: ComputerDesktopIcon,
          pages: [{ name: 'Lawfirm', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.LAWFIRM }]
        },
        {
          name: 'RA-MICRO',
          icon: ComputerDesktopIcon,
          pages: [{ name: 'RA-MICRO', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.RA_MICRO }]
        }
      ].sort((a, b) => a.name.localeCompare(b.name))
    }
  ])

  const topics = computed(() => {
    return availableTopics.value.filter((topic) => {
      if (topic.topic == SETTING_TOPICS.AI) {
        return user.value?.org.enable_law_specific_data
      }
      return true
    })
  })

  const selectedTopic = ref<Topic | null>(null) // Initially no topic is selected
  const selectedSetting = ref<Setting | null>(null) // Initially no setting is selected
  const selectedPage = ref<Page | null>(null) // Initially no page is selected
  provide('selectedTopic', selectedTopic)
  provide('selectedSetting', selectedSetting)
  provide('selectedPage', selectedPage)

  const updateSelections = () => {
    const segments = route.path.split('/').filter((segment) => segment)
    const topicSegment = segments[2]
    const pageSegment = segments[3] || null
    if (segments.length > 1) {
      selectedTopic.value = topics.value.find((setting) => setting.topic === topicSegment) as Topic
      if (selectedTopic.value && pageSegment) {
        selectedSetting.value = selectedTopic.value.settings.find((setting) =>
          setting.pages.some((p) => p.page === pageSegment)
        ) as Setting
        if (selectedSetting.value) {
          selectedPage.value = selectedSetting.value.pages.find(
            (p) => p.page === pageSegment
          ) as Page
        }
      }
    }
  }

  watch(route, () => {
    updateSelections()
  })

  onBeforeMount(() => {
    updateSelections()
  })

  onMounted(async () => {
    const fetching = []
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
    updateSelections()
  })
</script>

<template>
  <SpaCompatComponent v-if="user">
    <div class="">
      <div class="flex">
        <div class="w-1/4 items-center border-e-[1px]">
          <div class="border-b-[1px]">
            <div class="flex h-[70px] items-center justify-between gap-2">
              <h1 class="text-lg font-semibold text-stone-800 dark:text-neutral-200 md:text-xl">
                Einstellungen
              </h1>
            </div>
          </div>
          <nav class="flex w-full flex-col flex-wrap pb-3 pr-5 pt-5">
            <ul class="space-y-1.5">
              <!-- Topics List -->
              <li
                v-for="topic in topics"
                :key="topic.name"
                class="mb-1.5"
              >
                <button
                  type="button"
                  :class="selectedTopic === topic ? 'bg-slate-200' : ''"
                  class="flex w-full gap-x-3 rounded-lg px-3 py-2 text-start text-sm text-gray-800 hover:bg-slate-200
                    focus:outline-none"
                  @click="
                    () => {
                      selectedTopic = topic
                      selectedSetting = topic.settings[0]
                      selectedPage = selectedSetting.pages[0]
                      router.push(`/app/settings/${topic.topic}/${selectedPage.page}`)
                    }
                  "
                >
                  <!-- Topic Icon placed here -->
                  <component
                    :is="topic.icon"
                    class="size-4 flex-shrink-0"
                  />

                  {{ topic.name }}

                  <ChevronDownIcon
                    v-if="selectedTopic === topic"
                    class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                  />
                  <ChevronUpIcon
                    v-else
                    class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                  />
                </button>

                <!-- Settings that belong to the selected topic -->
                <div
                  v-show="selectedTopic === topic"
                  class="transition-[height] duration-300"
                >
                  <ul
                    class="relative mt-1.5 space-y-1.5 ps-7 before:absolute before:start-[18px] before:top-0 before:h-full
                      before:w-0.5 before:bg-slate-200"
                  >
                    <!-- Update this section inside the <li> for settings. -->
                    <li
                      v-for="setting in topic.settings"
                      :key="setting.name"
                      class="flex flex-col gap-y-2"
                    >
                      <button
                        type="button"
                        class="flex w-full gap-x-3 rounded-lg px-3 py-2 text-start text-sm text-gray-800 hover:bg-slate-200
                          focus:bg-slate-200 focus:outline-none"
                        :class="selectedSetting === setting ? 'bg-slate-200' : ''"
                        @click="
                          () => {
                            selectedSetting = setting
                            selectedPage = setting.pages[0] // default page when you click on the setting
                            router.push(`/app/settings/${topic.topic}/${selectedPage.page}`)
                          }
                        "
                      >
                        <!-- Setting Icon -->
                        <component
                          :is="setting.icon"
                          class="size-4 flex-shrink-0"
                        />
                        {{ setting.name }}

                        <!-- Display Chevron if the setting has more than one page -->
                        <ChevronDownIcon
                          v-if="selectedSetting === setting && setting.pages.length > 1"
                          class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                        />
                        <ChevronUpIcon
                          v-else-if="setting.pages.length > 1"
                          class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                        />
                      </button>

                      <!-- Page Links if there are multiple pages in the setting -->
                      <ul
                        v-show="selectedSetting === setting && setting.pages.length > 1"
                        class="relative mt-1.5 space-y-1.5 ps-4 before:absolute before:start-[18px] before:top-0 before:h-full
                          before:w-0.5 before:bg-slate-200"
                      >
                        <li
                          v-for="page in setting.pages"
                          :key="page.name"
                          class="ml-4 flex cursor-pointer gap-x-4 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-slate-200
                            focus:bg-slate-200"
                          :class="selectedPage === page ? 'bg-slate-200' : ''"
                          @click="
                            () => {
                              selectedPage = page
                              router.push(`/app/settings/${topic.topic}/${page.page}`)
                            }
                          "
                        >
                          <button type="button">
                            {{ page.name }}
                          </button>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </nav>
        </div>
        <div class="w-3/4">
          <router-view></router-view>
        </div>
      </div>
    </div>
  </SpaCompatComponent>
</template>

<style scoped>
  /* Scoped styling if needed */
</style>
